export interface YoutubeVideoReference {
  id: string;
  start: number;
}

const VIDEO_ID = /^[A-Za-z0-9_-]{6,32}$/;

function parseTime(value: string | null): number {
  if (!value) return 0;
  if (/^\d+$/.test(value)) return Number(value);
  const match = value.toLowerCase().match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!match) return 0;
  return Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0);
}

function withScheme(input: string): string {
  return /^[a-z][a-z\d+.-]*:\/\//i.test(input) ? input : `https://${input}`;
}

export function normalizeYoutubeUrl(input: string): YoutubeVideoReference | null {
  const value = input.trim();
  if (!value) return null;
  let url: URL;
  try {
    url = new URL(withScheme(value));
  } catch (_error) {
    return null;
  }
  const host = url.hostname.toLowerCase().replace(/^www\./, '').replace(/^m\./, '');
  let id = '';
  if (host === 'youtu.be') id = url.pathname.split('/').filter(Boolean)[0] || '';
  else if (host === 'youtube.com') {
    const parts = url.pathname.split('/').filter(Boolean);
    if (url.pathname === '/watch') id = url.searchParams.get('v') || '';
    else if (parts.length >= 2 && ['embed', 'shorts', 'live'].indexOf(parts[0]) >= 0) id = parts[1];
  } else return null;
  if (!VIDEO_ID.test(id)) return null;
  return { id, start: parseTime(url.searchParams.get('start') || url.searchParams.get('t')) };
}

export function youtubeEmbedUrl(video: YoutubeVideoReference): string {
  return `https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&start=${Math.max(0, Math.floor(video.start))}`;
}

export function youtubeThumbnailUrl(id: string): string {
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

// board poster only: 1280x720 and true 16:9, where hqdefault is a 480x360 4:3 frame with bars baked in.
// Not every video has one - a miss serves a 120x90 grey placeholder, so the caller size-checks and falls back.
export function youtubePosterUrl(id: string): string {
  return `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
}
